// Géométrie du plan annoté : échelle d'impression et anti-chevauchement des pins.
// Fonctions pures uniquement (ni Konva ni DOM), pour rester testables en Vitest.
// Le dessin lui-même est dans plan-export.ts.

// ─────────────────────────────────────────────
// Échelle d'impression
// ─────────────────────────────────────────────

/**
 * Boîte dans laquelle le plan est inséré dans le PDF (pdfmake `fit`, en points) :
 * largeur utile A4 × hauteur max sous le titre de zone et au-dessus de la légende.
 * Source unique : buildPlanImage (pdf-sections.ts) ET le calcul d'échelle ci-dessous
 * l'utilisent, sinon les tailles en mm dessinées sur le plan ne sont pas celles
 * réellement imprimées.
 */
export const PLAN_PDF_FIT_PT: [number, number] = [515, 600];

const MM_PER_PT = 25.4 / 72;
export const PLAN_PRINT_WIDTH_MM = PLAN_PDF_FIT_PT[0] * MM_PER_PT; // ≈ 182 mm
export const PLAN_PRINT_HEIGHT_MM = PLAN_PDF_FIT_PT[1] * MM_PER_PT; // ≈ 212 mm

/**
 * Calcule le nombre de millimètres par pixel du plan une fois inséré dans le PDF.
 * Même règle que le `fit` de pdfmake : pleine largeur, sauf si le plan dépasse
 * alors la hauteur max, auquel cas c'est la hauteur qui contraint.
 * @param planWidth  Largeur du plan en pixels (après rotation)
 * @param planHeight Hauteur du plan en pixels (après rotation)
 */
export function computeMmPerPixel(planWidth: number, planHeight: number): number {
  return Math.min(PLAN_PRINT_WIDTH_MM / planWidth, PLAN_PRINT_HEIGHT_MM / planHeight);
}

// ─────────────────────────────────────────────
// Disposition des pins (export uniquement)
// ─────────────────────────────────────────────

export interface PinPosition {
  /** Point réel de la mesure sur le plan */
  origX: number;
  origY: number;
  /** Centre de la pastille numérotée */
  x: number;
  y: number;
  /** true = pastille déportée : le point réel est dessiné à part, relié par un filet */
  moved: boolean;
}

export interface PinLayoutOptions {
  pinRadius: number;
  /** Distance minimale entre deux centres de pastilles */
  minSeparation: number;
  /** Marge entre le bord d'une pastille et un point réel (rayon du point + jeu) */
  anchorClearance: number;
  /** Dimensions du plan : les pastilles restent entièrement à l'intérieur */
  bounds?: { width: number; height: number };
}

const DIRECTIONS = 16;
// Pas d'éloignement (en rayons de pin) quand aucune direction n'est libre
const DISTANCE_STEP_RADII = 0.5;
const MAX_DISTANCE_STEPS = 24;
// Rayon (en rayons de pin) dans lequel deux pins sont considérés du même paquet
const NEIGHBOUR_RADII = 3;
// Sans tolérance, les arrondis flottants refusent des positions exactement à la limite
const TOLERANCE = 1e-6;

function clamp(value: number, min: number, max: number): number {
  // Plan plus petit que 2 marges : on centre plutôt que de sortir du plan
  if (max < min) return (min + max) / 2;
  return Math.min(max, Math.max(min, value));
}

/**
 * Place les pastilles numérotées pour qu'on puisse toujours lire OÙ est chaque mesure.
 *
 * Règle garantie : aucune pastille ne recouvre le point réel d'un autre pin, et deux
 * pastilles ne se chevauchent pas. Un pin isolé garde sa pastille sur son point. Un
 * pin en conflit est "déporté" : sa pastille est posée à côté, assez loin pour que
 * son point réel reste visible (le dessin les relie par un filet).
 *
 * Avant, les pastilles étaient juste écartées du minimum : le point réel restait
 * caché sous sa pastille ou sous la voisine, on ne voyait plus où était la mesure.
 */
export function layoutPins(
  pins: { x: number; y: number }[],
  { pinRadius, minSeparation, anchorClearance, bounds }: PinLayoutOptions
): PinPosition[] {
  const n = pins.length;
  // Distance minimale entre le centre d'une pastille et un point réel à laisser visible
  const keepOut = pinRadius + anchorClearance;

  const positions: PinPosition[] = pins.map((p) => ({
    origX: p.x,
    origY: p.y,
    x: bounds ? clamp(p.x, pinRadius, bounds.width - pinRadius) : p.x,
    y: bounds ? clamp(p.y, pinRadius, bounds.height - pinRadius) : p.y,
    moved: false,
  }));
  const placed: boolean[] = new Array(n).fill(false);

  const inBounds = (x: number, y: number) =>
    !bounds ||
    (x >= pinRadius - TOLERANCE &&
      x <= bounds.width - pinRadius + TOLERANCE &&
      y >= pinRadius - TOLERANCE &&
      y <= bounds.height - pinRadius + TOLERANCE);

  const isFree = (i: number, x: number, y: number) => {
    for (let j = 0; j < n; j++) {
      if (j === i) continue;
      if (Math.hypot(x - pins[j].x, y - pins[j].y) < keepOut - TOLERANCE) return false;
      if (placed[j] && Math.hypot(x - positions[j].x, y - positions[j].y) < minSeparation - TOLERANCE) {
        return false;
      }
    }
    return true;
  };

  const neighboursOf = (i: number) => {
    const result: number[] = [];
    for (let j = 0; j < n; j++) {
      if (Math.hypot(pins[j].x - pins[i].x, pins[j].y - pins[i].y) < NEIGHBOUR_RADII * pinRadius) result.push(j);
    }
    return result;
  };

  // Les pins sans voisin proche d'abord : ils restent en place et servent d'obstacles fixes
  const order = pins.map((_, i) => i);
  const isIsolated = (i: number) =>
    pins.every((p, j) => j === i || Math.hypot(p.x - pins[i].x, p.y - pins[i].y) >= minSeparation);
  order.sort((a, b) => Number(isIsolated(b)) - Number(isIsolated(a)) || a - b);

  for (const i of order) {
    // 1) Pastille sur son point, si elle ne gêne personne
    if (isFree(i, positions[i].x, positions[i].y)) {
      placed[i] = true;
      continue;
    }

    // 2) Sinon, déport : on part vers l'extérieur du paquet pour que les filets
    //    rayonnent au lieu de se croiser. Dans un paquet de m pins, la distance de
    //    départ est celle d'un cercle où m pastilles tiennent côte à côte : un paquet
    //    de pins superposés donne ainsi une couronne régulière.
    const neighbours = neighboursOf(i);
    const m = neighbours.length;
    const cx = neighbours.reduce((sum, j) => sum + pins[j].x, 0) / m;
    const cy = neighbours.reduce((sum, j) => sum + pins[j].y, 0) / m;
    const outX = pins[i].x - cx;
    const outY = pins[i].y - cy;
    const distToCentre = Math.hypot(outX, outY);
    const preferredAngle =
      distToCentre > 0.05 * pinRadius
        ? Math.atan2(outY, outX)
        : -Math.PI / 2 + (2 * Math.PI * neighbours.indexOf(i)) / m;
    const ringRadius = m > 1 ? minSeparation / (2 * Math.sin(Math.PI / m)) : 0;
    const startDistance = Math.max(keepOut, ringRadius - distToCentre);

    search: for (let step = 0; step <= MAX_DISTANCE_STEPS; step++) {
      const distance = startDistance + step * DISTANCE_STEP_RADII * pinRadius;
      for (let k = 0; k < DIRECTIONS; k++) {
        // Alterner de part et d'autre de la direction préférée : 0, -1, +1, -2, +2…
        const offset = k % 2 === 0 ? k / 2 : -(k + 1) / 2;
        const angle = preferredAngle + (offset * 2 * Math.PI) / DIRECTIONS;
        const x = pins[i].x + Math.cos(angle) * distance;
        const y = pins[i].y + Math.sin(angle) * distance;
        if (inBounds(x, y) && isFree(i, x, y)) {
          positions[i].x = x;
          positions[i].y = y;
          positions[i].moved = true;
          break search;
        }
      }
    }
    // Aucune place trouvée (plan saturé) : la pastille reste sur son point
    placed[i] = true;
  }

  return positions;
}
